import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Competitor {
  id: string;
  tenantId: string;
  name: string;
  website: string | null;
  description: string | null;
  notes: string | null;
}

interface BattleCard {
  id: string;
  competitorId: string;
  keyDifferentiators: string[];
  commonObjections: unknown;
  whenTheyAppear: string | null;
  howToWin: string | null;
}

export function CompetitorsPage() {
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [selected, setSelected] = useState<Competitor | null>(null);
  const [battleCard, setBattleCard] = useState<BattleCard | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [editCard, setEditCard] = useState(false);
  const [cardForm, setCardForm] = useState({
    keyDifferentiators: '',
    whenTheyAppear: '',
    howToWin: '',
  });

  const load = useCallback(async () => {
    const { data } = await apiClient.get<Competitor[]>('/competitors');
    setCompetitors(data);
  }, []);

  useEffect(() => {
    load().catch(() => setCompetitors([]));
  }, [load]);

  async function createCompetitor() {
    if (!newName.trim()) return;
    await apiClient.post('/competitors', { name: newName.trim() });
    setNewName('');
    setCreating(false);
    await load();
  }

  async function selectCompetitor(c: Competitor) {
    setSelected(c);
    setEditCard(false);
    try {
      const { data } = await apiClient.get<BattleCard>(`/competitors/${c.id}/battle-card`);
      setBattleCard(data);
      setCardForm({
        keyDifferentiators: data.keyDifferentiators.join('\n'),
        whenTheyAppear: data.whenTheyAppear ?? '',
        howToWin: data.howToWin ?? '',
      });
    } catch {
      setBattleCard(null);
      setCardForm({ keyDifferentiators: '', whenTheyAppear: '', howToWin: '' });
    }
  }

  async function saveCard() {
    if (!selected) return;
    await apiClient.put(`/competitors/${selected.id}/battle-card`, {
      keyDifferentiators: cardForm.keyDifferentiators
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      commonObjections: [],
      whenTheyAppear: cardForm.whenTheyAppear || null,
      howToWin: cardForm.howToWin || null,
    });
    setEditCard(false);
    await selectCompetitor(selected);
  }

  async function deleteCompetitor(id: string) {
    if (!confirm('Eliminar competidor?')) return;
    await apiClient.delete(`/competitors/${id}`);
    if (selected?.id === id) setSelected(null);
    await load();
  }

  return (
    <div className="flex h-full gap-6">
      {/* List pane */}
      <div className="w-64 shrink-0 space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">Competidores</h1>
          <Button size="sm" onClick={() => setCreating(true)}>+</Button>
        </div>

        {creating && (
          <div className="flex gap-2">
            <Input
              className="h-7 text-sm"
              placeholder="Nombre"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createCompetitor()}
              autoFocus
            />
            <Button size="sm" onClick={createCompetitor}>OK</Button>
          </div>
        )}

        <ul className="divide-y rounded-lg border bg-card">
          {competitors.map((c) => (
            <li
              key={c.id}
              className={`flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-muted/50 ${
                selected?.id === c.id ? 'bg-muted' : ''
              }`}
              onClick={() => selectCompetitor(c)}
            >
              <span className="text-sm font-medium">{c.name}</span>
              <button
                className="text-xs text-muted-foreground hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); deleteCompetitor(c.id); }}
              >
                ×
              </button>
            </li>
          ))}
          {competitors.length === 0 && (
            <li className="px-3 py-4 text-center text-sm text-muted-foreground">—</li>
          )}
        </ul>
      </div>

      {/* Detail pane */}
      {selected ? (
        <div className="flex-1 space-y-6">
          <div>
            <h2 className="text-xl font-semibold">{selected.name}</h2>
            {selected.website && (
              <a
                href={selected.website}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {selected.website}
              </a>
            )}
            {selected.description && (
              <p className="mt-2 text-sm text-muted-foreground">{selected.description}</p>
            )}
          </div>

          {/* Battle card */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Battle Card
              </h3>
              <Button size="sm" variant="outline" onClick={() => setEditCard((e) => !e)}>
                {editCard ? 'Cancelar' : battleCard ? 'Editar' : 'Crear'}
              </Button>
            </div>

            {editCard ? (
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Diferenciadores clave (uno por línea)
                  </label>
                  <textarea
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    rows={4}
                    value={cardForm.keyDifferentiators}
                    onChange={(e) => setCardForm({ ...cardForm, keyDifferentiators: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Cuándo aparecen
                  </label>
                  <textarea
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    rows={3}
                    value={cardForm.whenTheyAppear}
                    onChange={(e) => setCardForm({ ...cardForm, whenTheyAppear: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Cómo ganarles
                  </label>
                  <textarea
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    rows={3}
                    value={cardForm.howToWin}
                    onChange={(e) => setCardForm({ ...cardForm, howToWin: e.target.value })}
                  />
                </div>
                <Button size="sm" onClick={saveCard}>Guardar</Button>
              </div>
            ) : battleCard ? (
              <div className="space-y-4">
                {battleCard.keyDifferentiators.length > 0 && (
                  <div>
                    <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                      Diferenciadores
                    </div>
                    <ul className="list-inside list-disc space-y-0.5 text-sm">
                      {battleCard.keyDifferentiators.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {battleCard.whenTheyAppear && (
                  <div>
                    <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                      Cuándo aparecen
                    </div>
                    <p className="text-sm">{battleCard.whenTheyAppear}</p>
                  </div>
                )}
                {battleCard.howToWin && (
                  <div>
                    <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                      Cómo ganarles
                    </div>
                    <p className="text-sm">{battleCard.howToWin}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sin battle card aún.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          Selecciona un competidor
        </div>
      )}
    </div>
  );
}
